import React, { useState, useEffect, useMemo, useCallback } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Button,
  Grid,
  Avatar,
  Card,
  Icon,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import Male from "../../../assets/images/male.png";
import Female from "../../../assets/images/female.png";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import MDSnackbar from "components/MDSnackbar";
import { fetchEducationDetailsByParent } from "../../../services/educationService";
import { fetchWorkDetailsByParent } from "../../../services/workService";
import { fetchIDCardDetailsByParent } from "../../../services/idCardService";
import { fetchBankDetailsByParent } from "../../../services/bankDetailsService";
import { fetchSocialLinksByParent } from "../../../services/socialLinksService";
import { fetchAddressDetailsByParent } from "../../../services/addressService";
import { AddBankDialog, EditBankDialog, DeleteBankDialog } from "../../../components/BankDetails";
import { AddIDCardDialog, EditIDCardDialog, DeleteIDCardDialog } from "../../../components/IDCards";
import { AddWorkExperienceDialog, EditWorkExperienceDialog, DeleteWorkExperienceDialog } from "../../../components/WorkExperience";
import { AddEducationDialog, EditEducationDialog, DeleteEducationDialog } from "../../../components/Education";
import { AddSocialLinkDialog, EditSocialLinkDialog, DeleteSocialLinkDialog } from "../../../components/SocialLinks";
import { AddAddressDialog, EditAddressDialog, DeleteAddressDialog } from "../../../components/Address";
import { getSocialPlatformConfig } from "../../../utils/socialLinks";
import ShareProfileDialog from "../../../components/ShareProfileDialog";

// Helper function to decode HTML entities
const decodeHTMLEntities = (text) => {
  if (!text) return text;
  const textArea = document.createElement('textarea');
  textArea.innerHTML = text;
  return textArea.value;
};

const ViewFamilyMemberDialog = ({ open, onClose, member, sessionId, userId, user, userPhoto, onEdit, refreshCallback, familyMembers = [] }) => {
  // State for related data
  const [educationDetails, setEducationDetails] = useState([]);
  const [workDetails, setWorkDetails] = useState([]);
  const [idCardDetails, setIDCardDetails] = useState([]);
  const [bankDetails, setBankDetails] = useState([]);
  const [socialLinksDetails, setSocialLinksDetails] = useState([]);
  const [addressDetails, setAddressDetails] = useState([]);
  
  // State for visibility toggles
  const [visibleIDCards, setVisibleIDCards] = useState({});
  const [visibleBankAccounts, setVisibleBankAccounts] = useState({});
  
  // State for dialogs
  const [addEducationDialogOpen, setAddEducationDialogOpen] = useState(false);
  const [editingEducationEntry, setEditingEducationEntry] = useState(null);
  const [deleteEducationConfirmOpen, setDeleteEducationConfirmOpen] = useState(false);
  const [educationToDelete, setEducationToDelete] = useState(null);
  
  const [addWorkDialogOpen, setAddWorkDialogOpen] = useState(false);
  const [editingWorkEntry, setEditingWorkEntry] = useState(null);
  const [deleteWorkConfirmOpen, setDeleteWorkConfirmOpen] = useState(false);
  const [workToDelete, setWorkToDelete] = useState(null);
  
  const [addIDCardDialogOpen, setAddIDCardDialogOpen] = useState(false);
  const [editingIDCardEntry, setEditingIDCardEntry] = useState(null);
  const [deleteIDCardConfirmOpen, setDeleteIDCardConfirmOpen] = useState(false);
  const [idCardToDelete, setIdCardToDelete] = useState(null);
  
  const [addBankDialogOpen, setAddBankDialogOpen] = useState(false);
  const [editingBankEntry, setEditingBankEntry] = useState(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [bankToDelete, setBankToDelete] = useState(null);
  
  const [addSocialLinkDialogOpen, setAddSocialLinkDialogOpen] = useState(false);
  const [editingSocialLinkEntry, setEditingSocialLinkEntry] = useState(null);
  const [deleteSocialLinkConfirmOpen, setDeleteSocialLinkConfirmOpen] = useState(false);
  const [socialLinkToDelete, setSocialLinkToDelete] = useState(null);
  
  const [addAddressDialogOpen, setAddAddressDialogOpen] = useState(false);
  const [editingAddressEntry, setEditingAddressEntry] = useState(null);
  const [deleteAddressConfirmOpen, setDeleteAddressConfirmOpen] = useState(false);
  const [addressToDelete, setAddressToDelete] = useState(null);
  
  // Snackbar state
  const [successSB, setSuccessSB] = useState(false);
  const [errorSB, setErrorSB] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  // Share dialog state
  const [shareOpen, setShareOpen] = useState(false);

  // Fetch all related data when dialog opens or member changes
  useEffect(() => {
    if (!open || !member || !sessionId) return;

    const fetchAllData = async () => {
      // Determine the parentId
      // For "Myself" (id === "0" or id === 0), use the logged-in userId
      // For family members, use their record_id or id
      const isMySelf = member.id === "0" || member.id === 0 || member.relationship?.toLowerCase() === "myself";
      const parentId = isMySelf ? userId : (member.record_id || member.id);

      if (!parentId) {
        console.warn("No valid parentId for fetching related data");
        return;
      }

      console.log("🔍 Fetching related data for member:", member.name, "parentId:", parentId);

      try {
        // Fetch education
        const eduEntries = await fetchEducationDetailsByParent(sessionId, parentId);
        setEducationDetails(eduEntries || []);
        console.log("✅ Education entries:", eduEntries);
      } catch (error) {
        console.error("❌ Error fetching education:", error);
        setEducationDetails([]);
      }

      try {
        // Fetch work
        const workEntries = await fetchWorkDetailsByParent(sessionId, parentId);
        setWorkDetails(workEntries || []);
        console.log("✅ Work entries:", workEntries);
      } catch (error) {
        console.error("❌ Error fetching work:", error);
        setWorkDetails([]);
      }

      try {
        // Fetch ID cards
        const idCardEntries = await fetchIDCardDetailsByParent(sessionId, parentId);
        setIDCardDetails(idCardEntries || []);
        console.log("✅ ID Card entries:", idCardEntries);
      } catch (error) {
        console.error("❌ Error fetching ID cards:", error);
        setIDCardDetails([]);
      }

      try {
        // Fetch bank details
        const bankEntries = await fetchBankDetailsByParent(sessionId, parentId);
        setBankDetails(bankEntries || []);
        console.log("✅ Bank entries:", bankEntries);
      } catch (error) {
        console.error("❌ Error fetching bank details:", error);
        setBankDetails([]);
      }

      try {
        // Fetch social links
        const socialLinksEntries = await fetchSocialLinksByParent(sessionId, parentId);
        setSocialLinksDetails(socialLinksEntries || []);
        console.log("✅ Social Links entries:", socialLinksEntries);
      } catch (error) {
        console.error("❌ Error fetching social links:", error);
        setSocialLinksDetails([]);
      }

      try {
        // Fetch address details
        const addressEntries = await fetchAddressDetailsByParent(sessionId, parentId);
        setAddressDetails(addressEntries || []);
        console.log("✅ Address entries:", addressEntries);
      } catch (error) {
        console.error("❌ Error fetching address details:", error);
        setAddressDetails([]);
      }
    };

    fetchAllData();
  }, [open, member, sessionId, userId]);

  // Determine the display name - member object already has the name resolved
  const displayName = member?.name || "N/A";

  const isMyselfFamilyMember = useCallback((entry) => {
    if (!entry || userId == null) return false;
    const linkedId = entry.linkedUserId ?? entry.linked_user_id_c ?? entry.linked_user_id ?? entry.linked_userid;
    if (linkedId != null) return String(linkedId) === String(userId);
    const ownerId = entry.userId ?? entry.users_id_c ?? entry.owner_id;
    if (ownerId != null) return String(ownerId) === String(userId);
    return String(entry.id) === String(userId);
  }, [userId]);

  const idToFamilyMember = useMemo(() => {
    const map = new Map();
    (Array.isArray(familyMembers) ? familyMembers : []).forEach((entry) => {
      if (entry?.id != null) {
        map.set(String(entry.id), entry);
      }
    });
    return map;
  }, [familyMembers]);

  const collectParentIdentifiers = useCallback((entry) => {
    if (!entry) return [];
    return [
      entry.id,
      entry.record_id,
      entry.userId,
      entry.users_id_c,
      entry.user_id,
      entry.owner_id,
      entry.linkedUserId,
      entry.linked_user_id_c,
      entry.linked_user_id,
      entry.linked_userid,
      entry.spouseid_c,
      entry.spouseId,
      entry.parentid_c,
      entry.parentId,
    ]
      .filter((value) => value !== null && value !== undefined && String(value).trim() !== "")
      .map((value) => String(value));
  }, []);

  const findSpouseMember = useCallback((entry) => {
    if (!entry) return null;
    const entryId = entry.id != null ? String(entry.id) : null;
    const spouseId = (entry.spouseid_c ?? entry.spouseId)
      ? String(entry.spouseid_c ?? entry.spouseId)
      : null;

    if (spouseId && idToFamilyMember.has(spouseId)) {
      return idToFamilyMember.get(spouseId) || null;
    }

    for (const candidate of idToFamilyMember.values()) {
      const inverseSpouseId = candidate?.spouseid_c ?? candidate?.spouseId;
      if (inverseSpouseId && entryId && String(inverseSpouseId) === entryId) {
        return candidate || null;
      }
    }

    if (isMyselfFamilyMember(entry)) {
      const allMembers = Array.isArray(familyMembers) ? familyMembers : [];
      const spouseEntry = allMembers.find((candidate) => {
        const relation = String(candidate?.relationship || candidate?.relation || candidate?.relationshipType || '').toLowerCase();
        return relation === 'husband' || relation === 'wife';
      });
      return spouseEntry || null;
    }

    return null;
  }, [familyMembers, idToFamilyMember, isMyselfFamilyMember]);

  const findParentText = useCallback((entry) => {
    if (!entry) return null;
    const parentId = (entry.parentid_c ?? entry.parentId) ? String(entry.parentid_c ?? entry.parentId) : null;
    if (parentId && idToFamilyMember.has(parentId)) {
      const parentMember = idToFamilyMember.get(parentId);
      const names = [];
      if (parentMember?.name) names.push(parentMember.name);
      const parentSpouse = findSpouseMember(parentMember);
      if (parentSpouse?.name) names.push(parentSpouse.name);
      const uniqueNames = [...new Set(names.filter(Boolean))];
      return uniqueNames.length ? uniqueNames.join(' • ') : null;
    }

    if (isMyselfFamilyMember(entry)) {
      const allMembers = Array.isArray(familyMembers) ? familyMembers : [];
      const father = allMembers.find((item) => String(item?.relationship || item?.relationshipType || '').toLowerCase() === 'father');
      const mother = allMembers.find((item) => String(item?.relationship || item?.relationshipType || '').toLowerCase() === 'mother');
      const names = [father?.name, mother?.name].filter(Boolean);
      if (names.length) return names.join(' • ');
    }

    return null;
  }, [familyMembers, findSpouseMember, idToFamilyMember, isMyselfFamilyMember]);

  const findSpouseName = useCallback((entry) => {
    const spouseMember = findSpouseMember(entry);
    return spouseMember?.name || null;
  }, [findSpouseMember]);

  const findChildrenText = useCallback((entry) => {
    if (!entry) return null;
    const allMembers = Array.isArray(familyMembers) ? familyMembers : [];
    const entryId = entry.id != null ? String(entry.id) : null;
    const spouseMember = findSpouseMember(entry);
    const parentIds = new Set([
      entryId,
      spouseMember?.id != null ? String(spouseMember.id) : null,
    ].filter(Boolean));

    const childNames = allMembers
      .filter((candidate) => {
        if (!candidate || candidate.id == null || String(candidate.id) === entryId) return false;
        const parentId = candidate.parentId ?? candidate.parentid_c;
        return parentId != null && parentIds.has(String(parentId));
      })
      .map((candidate) => candidate.name)
      .filter(Boolean);

    const uniqueChildren = [...new Set(childNames)];
    if (!uniqueChildren.length) return null;
    return uniqueChildren.join(' • ');
  }, [familyMembers, findSpouseMember]);

  if (!member) return null;

  const parentText = findParentText(member);
  const spouseName = findSpouseName(member);
  const childrenText = findChildrenText(member);
  
  // Determine parentId for CRUD operations
  const isMySelf = member.id === "0" || member.id === 0 || member.relationship?.toLowerCase() === "myself";
  const parentId = isMySelf ? userId : (member.record_id || member.id);

  // Determine the photo
  const displayPhoto =
    member.id === "0"
      ? userPhoto || member.photo || (member.gender === "Female" ? Female : Male)
      : member.photo ||
        ([
          "Wife",
          "Sister",
          "Mother",
          "Grandmother",
          "Daughter",
          "Aunt",
          "Niece",
        ].includes(member.relationship)
          ? Female
          : Male);

  // Toggle visibility for sensitive data
  const toggleIDCardVisibility = (cardId) => {
    setVisibleIDCards((prev) => ({
      ...prev,
      [cardId]: !prev[cardId],
    }));
  };

  const toggleBankAccountVisibility = (accountId) => {
    setVisibleBankAccounts((prev) => ({
      ...prev,
      [accountId]: !prev[accountId],
    }));
  };

  // Helper to mask sensitive data
  const maskData = (data, visible) => {
    if (!data || visible) return data;
    return "••••••••••";
  };

  const ImagePreview = ({ src, label }) => {
    if (!src || typeof src !== "string") return null;
    const trimmedSrc = src.trim();
    if (!trimmedSrc.startsWith("data:")) return null;

    return (
      <Grid item xs={12} sm={6}>
        <MDTypography variant="caption" color="text" fontWeight="bold">
          {label}
        </MDTypography>
        <MDBox
          component="a"
          href={trimmedSrc}
          target="_blank"
          rel="noopener noreferrer"
          sx={{
            display: "inline-block",
            mt: 1,
            borderRadius: 1,
            overflow: "hidden",
            border: "1px solid rgba(0, 0, 0, 0.12)",
            boxShadow: 1,
            transition: "box-shadow 0.2s ease",
            "&:hover": {
              boxShadow: 3,
            },
          }}
        >
          <MDBox
            component="img"
            src={trimmedSrc}
            alt={label}
            sx={{
              display: "block",
              width: "100%",
              maxWidth: 260,
              maxHeight: 180,
              objectFit: "contain",
              backgroundColor: "#fafafa",
            }}
          />
        </MDBox>
      </Grid>
    );
  };

  // Refresh data after CRUD operations
  const refreshData = async () => {
    if (!parentId || !sessionId) return;

    try {
      const [eduEntries, workEntries, idCardEntries, bankEntries, socialLinksEntries, addressEntries] = await Promise.all([
        fetchEducationDetailsByParent(sessionId, parentId),
        fetchWorkDetailsByParent(sessionId, parentId),
        fetchIDCardDetailsByParent(sessionId, parentId),
        fetchBankDetailsByParent(sessionId, parentId),
        fetchSocialLinksByParent(sessionId, parentId),
        fetchAddressDetailsByParent(sessionId, parentId),
      ]);

      setEducationDetails(eduEntries || []);
      setWorkDetails(workEntries || []);
      setIDCardDetails(idCardEntries || []);
      setBankDetails(bankEntries || []);
      setSocialLinksDetails(socialLinksEntries || []);
      setAddressDetails(addressEntries || []);

      // Call refresh callback if provided
      if (refreshCallback) {
        refreshCallback();
      }
    } catch (error) {
      console.error("Error refreshing data:", error);
    }
  };

  const DetailRow = ({ label, value }) => (
    <Grid item xs={12} sm={6}>
      <div className="mb-4">
        <p className="text-sm font-semibold text-gray-600 mb-1">{label}</p>
        <p className="text-base text-gray-800">{decodeHTMLEntities(value) || "Not specified"}</p>
      </div>
    </Grid>
  );

  const EmailRow = ({ label, email }) => (
    <Grid item xs={12} sm={6}>
      <div className="mb-4">
        <p className="text-sm font-semibold text-gray-600 mb-1">{label}</p>
        {email ? (
          <a
            href={`mailto:${email}`}
            className="text-blue-600 hover:text-blue-800 hover:underline font-medium inline-flex items-center gap-1"
          >
            <span className="text-lg">📧</span>
            <span>{email}</span>
          </a>
        ) : (
          <p className="text-base text-gray-800">Not specified</p>
        )}
      </div>
    </Grid>
  );

  const MobileRow = ({ label, mobile }) => (
    <Grid item xs={12} sm={6}>
      <div className="mb-4">
        <p className="text-sm font-semibold text-gray-600 mb-1">{label}</p>
        {mobile ? (
          <a
            href={`tel:${mobile}`}
            className="text-blue-600 hover:text-blue-800 hover:underline font-medium inline-flex items-center gap-1"
          >
            <span className="text-lg">📞</span>
            <span>{mobile}</span>
          </a>
        ) : (
          <p className="text-base text-gray-800">Not specified</p>
        )}
      </div>
    </Grid>
  );

  // Helper to render education detail card
  const renderEducationDetail = (entry, index) => {
    if (!entry) return null;

    return (
      <Card
        key={entry.id || index}
        sx={{
          p: 2,
          mb: 2,
          boxShadow: 2,
          height: "100%",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <MDBox display="flex" justifyContent="space-between" alignItems="center" mb={1}>
          <MDTypography variant="h6" fontWeight="medium" color="info">
            🎓 {decodeHTMLEntities(entry.qualification || entry.name || `Education ${index + 1}`)}
          </MDTypography>
          <MDBox display="flex" gap={1}>
            <IconButton
              size="small"
              color="info"
              onClick={() => setEditingEducationEntry(entry)}
              sx={{ p: 0.5 }}
            >
              <Icon fontSize="small">edit</Icon>
            </IconButton>
            <IconButton
              size="small"
              color="error"
              onClick={() => {
                setEducationToDelete(entry);
                setDeleteEducationConfirmOpen(true);
              }}
              sx={{ p: 0.5 }}
            >
              <Icon fontSize="small">delete</Icon>
            </IconButton>
          </MDBox>
        </MDBox>
        <Grid container spacing={2} sx={{ flexGrow: 1 }}>
          {entry.qualification && (
            <Grid item xs={12} sm={6}>
              <MDTypography variant="caption" color="text" fontWeight="bold">
                Qualification
              </MDTypography>
              <MDTypography variant="body2">{decodeHTMLEntities(entry.qualification)}</MDTypography>
            </Grid>
          )}
          {entry.board && (
            <Grid item xs={12} sm={6}>
              <MDTypography variant="caption" color="text" fontWeight="bold">
                Board/University
              </MDTypography>
              <MDTypography variant="body2">{decodeHTMLEntities(entry.board)}</MDTypography>
            </Grid>
          )}
          {entry.course && (
            <Grid item xs={12} sm={6}>
              <MDTypography variant="caption" color="text" fontWeight="bold">
                Course
              </MDTypography>
              <MDTypography variant="body2">{decodeHTMLEntities(entry.course)}</MDTypography>
            </Grid>
          )}
          {entry.educationtype && (
            <Grid item xs={12} sm={6}>
              <MDTypography variant="caption" color="text" fontWeight="bold">
                Education Type
              </MDTypography>
              <MDTypography variant="body2">{decodeHTMLEntities(entry.educationtype)}</MDTypography>
            </Grid>
          )}
          {entry.institute && (
            <Grid item xs={12} sm={6}>
              <MDTypography variant="caption" color="text" fontWeight="bold">
                Institute/School
              </MDTypography>
              <MDTypography variant="body2">{decodeHTMLEntities(entry.institute)}</MDTypography>
            </Grid>
          )}
          {(entry.instituecity || entry.institutestate || entry.institutecountry) && (
            <Grid item xs={12} sm={6}>
              <MDTypography variant="caption" color="text" fontWeight="bold">
                Location
              </MDTypography>
              <MDTypography variant="body2">
                {[
                  decodeHTMLEntities(entry.instituecity),
                  decodeHTMLEntities(entry.institutestate),
                  decodeHTMLEntities(entry.institutecountry),
                ]
                  .filter(Boolean)
                  .join(", ")}
              </MDTypography>
            </Grid>
          )}
          {(entry.startyear_c || entry.endyear_c) && (
            <Grid item xs={12} sm={6}>
              <MDTypography variant="caption" color="text" fontWeight="bold">
                Duration
              </MDTypography>
              <MDTypography variant="body2">
                {entry.startyear_c || "N/A"} -{" "}
                {entry.currentlystudying_c ? "Present" : entry.endyear_c || "N/A"}
              </MDTypography>
            </Grid>
          )}
          {entry.grade && (
            <Grid item xs={12} sm={6}>
              <MDTypography variant="caption" color="text" fontWeight="bold">
                Grade/Mark
              </MDTypography>
              <MDTypography variant="body2">{decodeHTMLEntities(entry.grade)}</MDTypography>
            </Grid>
          )}
          <ImagePreview src={entry.image} label="School/Institute ID" />
        </Grid>
      </Card>
    );
  };

  // Helper to render work detail card
  const renderWorkDetail = (entry, index) => {
    if (!entry) return null;

    const hasData =
      (entry.companyname && entry.companyname.trim()) ||
      (entry.designation && entry.designation.trim()) ||
      (entry.department && entry.department.trim());

    if (!hasData) return null;

    return (
      <Card
        key={entry.id || index}
        sx={{
          p: 2,
          mb: 2,
          boxShadow: 2,
          height: "100%",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <MDBox display="flex" justifyContent="space-between" alignItems="center" mb={1}>
          <MDTypography variant="h6" fontWeight="medium" color="primary">
            💼 {decodeHTMLEntities(entry.companyname || entry.name || `Work Experience ${index + 1}`)}
          </MDTypography>
          <MDBox display="flex" gap={1}>
            <IconButton
              size="small"
              color="info"
              onClick={() => setEditingWorkEntry(entry)}
              sx={{ p: 0.5 }}
            >
              <Icon fontSize="small">edit</Icon>
            </IconButton>
            <IconButton
              size="small"
              color="error"
              onClick={() => {
                setWorkToDelete(entry);
                setDeleteWorkConfirmOpen(true);
              }}
              sx={{ p: 0.5 }}
            >
              <Icon fontSize="small">delete</Icon>
            </IconButton>
          </MDBox>
        </MDBox>
        <Grid container spacing={2} sx={{ flexGrow: 1 }}>
          {entry.companyname && (
            <Grid item xs={12} sm={6}>
              <MDTypography variant="caption" color="text" fontWeight="bold">
                Company
              </MDTypography>
              <MDTypography variant="body2">{decodeHTMLEntities(entry.companyname)}</MDTypography>
            </Grid>
          )}
          {entry.designation && (
            <Grid item xs={12} sm={6}>
              <MDTypography variant="caption" color="text" fontWeight="bold">
                Designation
              </MDTypography>
              <MDTypography variant="body2">{decodeHTMLEntities(entry.designation)}</MDTypography>
            </Grid>
          )}
          {entry.department && (
            <Grid item xs={12} sm={6}>
              <MDTypography variant="caption" color="text" fontWeight="bold">
                Department
              </MDTypography>
              <MDTypography variant="body2">{decodeHTMLEntities(entry.department)}</MDTypography>
            </Grid>
          )}
          {entry.sector && (
            <Grid item xs={12} sm={6}>
              <MDTypography variant="caption" color="text" fontWeight="bold">
                Sector/Industry
              </MDTypography>
              <MDTypography variant="body2">{decodeHTMLEntities(entry.sector)}</MDTypography>
            </Grid>
          )}
          {entry.employmenttype && (
            <Grid item xs={12} sm={6}>
              <MDTypography variant="caption" color="text" fontWeight="bold">
                Employment Type
              </MDTypography>
              <MDTypography variant="body2">
                {decodeHTMLEntities(entry.employmenttype)}
              </MDTypography>
            </Grid>
          )}
          {(entry.startdate || entry.enddate || entry.currentlyworking) && (
            <Grid item xs={12} sm={6}>
              <MDTypography variant="caption" color="text" fontWeight="bold">
                Duration
              </MDTypography>
              <MDTypography variant="body2">
                {entry.startdate || "N/A"} -{" "}
                {entry.currentlyworking === true ||
                entry.currentlyworking === 1 ||
                entry.currentlyworking === "1"
                  ? "Present"
                  : entry.enddate || "N/A"}
              </MDTypography>
            </Grid>
          )}
          {(entry.city || entry.state || entry.country) && (
            <Grid item xs={12} sm={6}>
              <MDTypography variant="caption" color="text" fontWeight="bold">
                Location
              </MDTypography>
              <MDTypography variant="body2">
                {[
                  decodeHTMLEntities(entry.city),
                  decodeHTMLEntities(entry.state),
                  decodeHTMLEntities(entry.country),
                ]
                  .filter(Boolean)
                  .join(", ")}
              </MDTypography>
            </Grid>
          )}
          {entry.description && (
            <Grid item xs={12}>
              <MDTypography variant="caption" color="text" fontWeight="bold">
                Job Description
              </MDTypography>
              <MDTypography variant="body2">{decodeHTMLEntities(entry.description)}</MDTypography>
            </Grid>
          )}
          <ImagePreview src={entry.image} label="Work Document" />
        </Grid>
      </Card>
    );
  };

  // Helper to render ID card detail
  const renderIDCardDetail = (entry, index) => {
    if (!entry) return null;

    const cardId = entry.id || `idcard-${index}`;
    const isVisible = visibleIDCards[cardId] || false;

    const hasData =
      (entry.idtype && entry.idtype.trim()) ||
      (entry.name && entry.name.trim()) ||
      (entry.idnumber && entry.idnumber.trim());

    if (!hasData) return null;

    return (
      <Card
        key={cardId}
        sx={{
          p: 2,
          mb: 2,
          boxShadow: 2,
          height: "100%",
          display: "flex",
          flexDirection: "column",
          position: "relative",
        }}
      >
        <MDBox
          sx={{
            position: "absolute",
            top: 8,
            right: 8,
            display: "flex",
            gap: 1,
            zIndex: 1,
          }}
        >
          <IconButton
            size="small"
            onClick={() => setEditingIDCardEntry(entry)}
            sx={{ color: "primary.main" }}
          >
            <Icon fontSize="small">edit</Icon>
          </IconButton>
          <IconButton
            size="small"
            onClick={() => {
              setIdCardToDelete(entry);
              setDeleteIDCardConfirmOpen(true);
            }}
            sx={{ color: "error.main" }}
          >
            <Icon fontSize="small">delete</Icon>
          </IconButton>
          <IconButton
            size="small"
            onClick={() => toggleIDCardVisibility(cardId)}
            sx={{ color: isVisible ? "primary.main" : "secondary.main" }}
          >
            <Icon fontSize="small">
              {isVisible ? "visibility" : "visibility_off"}
            </Icon>
          </IconButton>
        </MDBox>

        <MDTypography variant="h6" fontWeight="medium" color="warning" mb={1} pr={4}>
          🆔 {decodeHTMLEntities(entry.idtype || `ID Card ${index + 1}`)}
        </MDTypography>
        
        <Grid container spacing={2} sx={{ flexGrow: 1 }}>
          {entry.name && (
            <Grid item xs={12} sm={6}>
              <MDTypography variant="caption" color="text" fontWeight="bold">
                Name on ID Card
              </MDTypography>
              <MDTypography variant="body2">{decodeHTMLEntities(entry.name)}</MDTypography>
            </Grid>
          )}
          {entry.idnumber && (
            <Grid item xs={12} sm={6}>
              <MDTypography variant="caption" color="text" fontWeight="bold">
                ID Number
              </MDTypography>
              <MDTypography variant="body2" sx={{ fontFamily: "monospace" }}>
                {maskData(entry.idnumber, isVisible)}
              </MDTypography>
            </Grid>
          )}
          {entry.issuedate && (
            <Grid item xs={12} sm={6}>
              <MDTypography variant="caption" color="text" fontWeight="bold">
                Issue Date
              </MDTypography>
              <MDTypography variant="body2">{entry.issuedate}</MDTypography>
            </Grid>
          )}
          {entry.expirydate && (
            <Grid item xs={12} sm={6}>
              <MDTypography variant="caption" color="text" fontWeight="bold">
                Expiry Date
              </MDTypography>
              <MDTypography variant="body2">
                {maskData(entry.expirydate, isVisible)}
              </MDTypography>
            </Grid>
          )}
          {entry.description && (
            <Grid item xs={12}>
              <MDTypography variant="caption" color="text" fontWeight="bold">
                Additional Notes
              </MDTypography>
              <MDTypography variant="body2">{decodeHTMLEntities(entry.description)}</MDTypography>
            </Grid>
          )}
          <ImagePreview src={entry.image1} label="Front Side" />
          <ImagePreview src={entry.image2} label="Back Side" />
        </Grid>
      </Card>
    );
  };

  // Helper to render bank detail
  const renderBankDetail = (entry, index) => {
    if (!entry) return null;

    const accountId = entry.id || `bank-${index}`;
    const isVisible = visibleBankAccounts[accountId] || false;

    const hasData =
      (entry.accountholdername && entry.accountholdername.trim()) ||
      (entry.accountnumber && entry.accountnumber.trim()) ||
      (entry.ifsc && entry.ifsc.trim()) ||
      (entry.bankname && entry.bankname.trim());

    if (!hasData) return null;

    return (
      <Card
        key={accountId}
        sx={{
          p: 2,
          mb: 2,
          boxShadow: 2,
          height: "100%",
          display: "flex",
          flexDirection: "column",
          position: "relative",
        }}
      >
        <MDBox
          sx={{
            position: "absolute",
            top: 8,
            right: 8,
            display: "flex",
            gap: 1,
            zIndex: 1,
          }}
        >
          <IconButton
            size="small"
            onClick={() => setEditingBankEntry(entry)}
            sx={{ color: "info.main" }}
          >
            <Icon fontSize="small">edit</Icon>
          </IconButton>
          <IconButton
            size="small"
            onClick={() => {
              setBankToDelete(entry);
              setDeleteConfirmOpen(true);
            }}
            sx={{ color: "error.main" }}
          >
            <Icon fontSize="small">delete</Icon>
          </IconButton>
          <IconButton
            size="small"
            onClick={() => toggleBankAccountVisibility(accountId)}
            sx={{ color: isVisible ? "primary.main" : "secondary.main" }}
          >
            <Icon fontSize="small">
              {isVisible ? "visibility" : "visibility_off"}
            </Icon>
          </IconButton>
        </MDBox>

        <MDTypography variant="h6" fontWeight="medium" color="success" mb={1} pr={4}>
          🏦 {decodeHTMLEntities(entry.bankname || `Bank Account ${index + 1}`)}
        </MDTypography>
        
        <Grid container spacing={2} sx={{ flexGrow: 1 }}>
          {entry.accountholdername && (
            <Grid item xs={12} sm={6}>
              <MDTypography variant="caption" color="text" fontWeight="bold">
                Account Holder Name
              </MDTypography>
              <MDTypography variant="body2">{decodeHTMLEntities(entry.accountholdername)}</MDTypography>
            </Grid>
          )}
          {entry.accountnumber && (
            <Grid item xs={12} sm={6}>
              <MDTypography variant="caption" color="text" fontWeight="bold">
                Account Number
              </MDTypography>
              <MDTypography variant="body2" sx={{ fontFamily: "monospace" }}>
                {maskData(entry.accountnumber, isVisible)}
              </MDTypography>
            </Grid>
          )}
          {entry.ifsc && (
            <Grid item xs={12} sm={6}>
              <MDTypography variant="caption" color="text" fontWeight="bold">
                IFSC Code
              </MDTypography>
              <MDTypography variant="body2" sx={{ fontFamily: "monospace" }}>
                {maskData(entry.ifsc, isVisible)}
              </MDTypography>
            </Grid>
          )}
          {entry.accounttype && (
            <Grid item xs={12} sm={6}>
              <MDTypography variant="caption" color="text" fontWeight="bold">
                Account Type
              </MDTypography>
              <MDTypography variant="body2">{decodeHTMLEntities(entry.accounttype)}</MDTypography>
            </Grid>
          )}
          {entry.branch && (
            <Grid item xs={12} sm={6}>
              <MDTypography variant="caption" color="text" fontWeight="bold">
                Branch
              </MDTypography>
              <MDTypography variant="body2">{decodeHTMLEntities(entry.branch)}</MDTypography>
            </Grid>
          )}
          {entry.bankcountry && (
            <Grid item xs={12} sm={6}>
              <MDTypography variant="caption" color="text" fontWeight="bold">
                Bank Country
              </MDTypography>
              <MDTypography variant="body2">{decodeHTMLEntities(entry.bankcountry)}</MDTypography>
            </Grid>
          )}
          <ImagePreview src={entry.image} label="Passbook / Document" />
        </Grid>
      </Card>
    );
  };


  // Helper to render social link card
  const renderSocialLink = (entry, index) => {
    if (!entry) return null;

    const hasType = entry.type && entry.type.trim();
    const hasLink = entry.link && entry.link.trim();

    if (!hasType && !hasLink) return null;

    const { platform, linkUrl } = getSocialPlatformConfig(entry);

    return (
      <Grid item xs={12} sm={6} md={4} lg={2.4} key={entry.id || `social-${index}`}>
        <MDBox position="relative">
          <MDBox
            className="action-buttons"
            sx={{
              position: "absolute",
              top: 8,
              right: 8,
              display: "flex",
              gap: 0.5,
              zIndex: 10,
            }}
          >
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                setEditingSocialLinkEntry(entry);
              }}
              sx={{
                "&:hover": { 
                  backgroundColor: "rgba(33, 150, 243, 0.1)",
                },
              }}
            >
              <Icon fontSize="small" color="info">edit</Icon>
            </IconButton>
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                setSocialLinkToDelete(entry);
                setDeleteSocialLinkConfirmOpen(true);
              }}
              sx={{
                "&:hover": { 
                  backgroundColor: "rgba(244, 67, 54, 0.1)",
                },
              }}
            >
              <Icon fontSize="small" color="error">delete</Icon>
            </IconButton>
          </MDBox>

          <a
            href={linkUrl}
            target="_blank"
            rel="noopener noreferrer"
            style={{ textDecoration: "none" }}
            onClick={(e) => {
              const target = e.target;
              if (target.closest('.action-buttons')) {
                e.preventDefault();
              }
            }}
          >
            <MDBox
              textAlign="center"
              p={2}
              borderRadius="lg"
              sx={{
                bgcolor: "#f5f5f5",
                cursor: "pointer",
                transition: "all 0.3s",
                "&:hover": {
                  bgcolor: platform.color,
                  transform: "translateY(-5px)",
                  boxShadow: 3,
                  "& .MuiIcon-root, & .MuiTypography-root": {
                    color: "white !important",
                  },
                },
              }}
            >
              <Icon fontSize="large" sx={{ color: platform.color }}>
                {platform.icon}
              </Icon>
              <MDTypography variant="button" display="block" mt={1}>
                {platform.displayName}
              </MDTypography>
              <MDTypography variant="caption" sx={{ wordBreak: "break-all" }}>
                {entry.link}
              </MDTypography>
            </MDBox>
          </a>
        </MDBox>
      </Grid>
    );
  };

  const SocialMediaLink = ({ icon, label, url }) => {
    if (!url) return null;
    
    // Ensure URL has protocol
    const formattedUrl = url.startsWith('http://') || url.startsWith('https://') 
      ? url 
      : `https://${url}`;
    
    return (
      <Grid item xs={12} sm={6}>
        <div className="mb-4">
          <p className="text-sm font-semibold text-gray-600 mb-1 flex items-center gap-2">
            <span>{icon}</span> {label}
          </p>
          <a
            href={formattedUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 text-blue-600 hover:text-blue-800 hover:underline break-all font-medium"
            style={{
              wordBreak: 'break-word',
              textDecoration: 'none',
            }}
          >
            <span className="text-lg">🔗</span>
            <span className="hover:underline">{url}</span>
          </a>
        </div>
      </Grid>
    );
  };

  // Helper to render address detail card
  const renderAddressDetail = (entry, index) => {
    if (!entry) return null;

    let addressType = entry.addresstype_c || entry.addresstype || `Address ${index + 1}`;
    if (addressType === 'Current') addressType = 'Current Address';
    if (addressType === 'Permanent') addressType = 'Permanent Address';

    return (
      <Card
        key={entry.id || index}
        sx={{
          p: 2,
          mb: 2,
          boxShadow: 2,
          height: "100%",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <MDBox display="flex" justifyContent="space-between" alignItems="center" mb={1}>
          <MDTypography variant="h6" fontWeight="medium" sx={{ color: '#ff9800' }}>
            🏠 {addressType}
          </MDTypography>
          <MDBox display="flex" gap={1}>
            <IconButton
              size="small"
              color="warning"
              onClick={() => setEditingAddressEntry(entry)}
              sx={{ p: 0.5 }}
            >
              <Icon fontSize="small">edit</Icon>
            </IconButton>
            <IconButton
              size="small"
              color="error"
              onClick={() => {
                setAddressToDelete(entry);
                setDeleteAddressConfirmOpen(true);
              }}
              sx={{ p: 0.5 }}
            >
              <Icon fontSize="small">delete</Icon>
            </IconButton>
          </MDBox>
        </MDBox>
        <Grid container spacing={2} sx={{ flexGrow: 1 }}>
          {entry.address && (
            <Grid item xs={12}>
              <MDTypography variant="caption" color="text" fontWeight="bold">
                Address
              </MDTypography>
              <MDTypography variant="body2">{decodeHTMLEntities(entry.address)}</MDTypography>
            </Grid>
          )}
          {entry.street && (
            <Grid item xs={12} sm={6}>
              <MDTypography variant="caption" color="text" fontWeight="bold">
                Street
              </MDTypography>
              <MDTypography variant="body2">{decodeHTMLEntities(entry.street)}</MDTypography>
            </Grid>
          )}
          {entry.village && (
            <Grid item xs={12} sm={6}>
              <MDTypography variant="caption" color="text" fontWeight="bold">
                Village/Town
              </MDTypography>
              <MDTypography variant="body2">{decodeHTMLEntities(entry.village)}</MDTypography>
            </Grid>
          )}
          {entry.pincode && (
            <Grid item xs={12} sm={6}>
              <MDTypography variant="caption" color="text" fontWeight="bold">
                Pincode
              </MDTypography>
              <MDTypography variant="body2">{entry.pincode}</MDTypography>
            </Grid>
          )}
          {entry.taluk && (
            <Grid item xs={12} sm={6}>
              <MDTypography variant="caption" color="text" fontWeight="bold">
                Taluk
              </MDTypography>
              <MDTypography variant="body2">{decodeHTMLEntities(entry.taluk)}</MDTypography>
            </Grid>
          )}
          {entry.district && (
            <Grid item xs={12} sm={6}>
              <MDTypography variant="caption" color="text" fontWeight="bold">
                District
              </MDTypography>
              <MDTypography variant="body2">{decodeHTMLEntities(entry.district)}</MDTypography>
            </Grid>
          )}
          {entry.state && (
            <Grid item xs={12} sm={6}>
              <MDTypography variant="caption" color="text" fontWeight="bold">
                State
              </MDTypography>
              <MDTypography variant="body2">{decodeHTMLEntities(entry.state)}</MDTypography>
            </Grid>
          )}
          {entry.country && (
            <Grid item xs={12} sm={6}>
              <MDTypography variant="caption" color="text" fontWeight="bold">
                Country
              </MDTypography>
              <MDTypography variant="body2">{decodeHTMLEntities(entry.country)}</MDTypography>
            </Grid>
          )}
        </Grid>
      </Card>
    );
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          maxHeight: "90vh",
          width: "90vw",
          maxWidth: "90vw",
        },
      }}
    >
      <DialogTitle
        sx={{
          backgroundColor: "#f5f5f5",
          borderBottom: "2px solid #e0e0e0",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          padding: "24px 32px",
        }}
      >
        <span className="text-2xl font-bold text-gray-800">Family Member Details</span>
        <IconButton
          onClick={onClose}
          sx={{
            color: "#757575",
            "&:hover": {
              backgroundColor: "#e0e0e0",
            },
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ padding: "32px" }}>
        {/* 🧍‍♂️ 1. Personal Details */}
        <div className="mb-6">
          <h3 className="text-xl font-semibold text-gray-800 mb-4 pb-2 border-b-2 border-blue-500">
            🧍‍♂️ 1. Personal Details
          </h3>
          
          {/* Photo */}
          <div className="flex justify-center mb-6">
            <Avatar
              src={displayPhoto}
              alt={displayName}
              sx={{
                width: 120,
                height: 120,
                border: "4px solid #e0e0e0",
                boxShadow: "0 4px 6px rgba(0,0,0,0.1)",
              }}
            />
          </div>

          <Grid container spacing={2}>
            <DetailRow label="Name" value={displayName} />
            <MobileRow label="Mobile Number" mobile={member.mobile_c || member.mobile} />
            <EmailRow label="Email" email={member.email_c || member.email} />
            <DetailRow label="Parent" value={parentText} />
            <DetailRow label="Spouse" value={spouseName} />
            <DetailRow label="Children" value={childrenText} />
            <DetailRow label="Aadhaar" value={member.aadhaar_c || member.aadhar_c || member.aadhar} />
            <DetailRow label="Caste" value={member.caste_c || member.caste} />
            <DetailRow label="Religion" value={member.religion_c || member.religion} />
            <DetailRow label="Date of Birth" value={member.dob_c} />
            <DetailRow label="Age" value={member.age_c} />
            <DetailRow label="Gender" value={member.gender_c} />
            <DetailRow label="Blood Group" value={member.bloodgroup_c} />
            <DetailRow label="Birth Place" value={member.birthplace_c} />
            <DetailRow label="Disability" value={member.disability_c} />
            {member.disability_c === "Yes" && (
              <DetailRow label="Disability Type" value={member.disabilitytype_c} />
            )}
            <DetailRow label="Alive Status" value={member.alivestatus_c} />
            {member.alivestatus_c === "Deceased" && (
              <>
                <DetailRow label="Date of Death" value={member.dateofdeath_c} />
                <DetailRow label="Death Place" value={member.deathplace_c} />
                <DetailRow label="Death Reason" value={member.deathreason_c} />
              </>
            )}
            <DetailRow label="Marital Status" value={member.maritalstatus_c} />
            {(member.maritalstatus_c === "Married" || member.maritalstatus_c === "Divorced" || member.maritalstatus_c === "Widowed") && (
              <>
                <DetailRow label="Marriage Date" value={member.marriagedate_c} />
                <DetailRow label="Marriage Place" value={member.marriageplace_c} />
              </>
            )}
            <DetailRow label="Economic Status" value={member.economical_status_c} />
          </Grid>
        </div>

        {/* 🗣️ 2. Language Details */}
        <div className="mb-6">
          <h3 className="text-xl font-semibold text-gray-800 mb-4 pb-2 border-b-2 border-green-500">
            🗣️ 2. Language Details
          </h3>
          <Grid container spacing={2}>
            <DetailRow label="Mother Tongue" value={member.mothertongue_c} />
            <DetailRow label="Languages Known" value={member.languagesknown_c} />
          </Grid>
        </div>

        {/* 🏠 3. Address Details - From Address Module */}
        <Card sx={{ boxShadow: 3, mb: 3 }}>
          <MDBox p={3}>
            <MDBox display="flex" alignItems="center" justifyContent="space-between" mb={3}>
              <MDBox display="flex" alignItems="center">
                <Icon fontSize="large" sx={{ color: '#ff9800' }}>
                  home
                </Icon>
                <MDTypography variant="h5" fontWeight="bold" ml={1}>
                  Address Details
                </MDTypography>
              </MDBox>
              <MDButton
                variant="gradient"
                sx={{ 
                  background: 'linear-gradient(195deg, #ff9800, #fb8c00)',
                  color: '#fff'
                }}
                size="small"
                onClick={() => setAddAddressDialogOpen(true)}
              >
                <Icon sx={{ mr: 0.5 }}>add</Icon>
                Add Address
              </MDButton>
            </MDBox>
            <Grid container spacing={2}>
              {addressDetails && addressDetails.length > 0 ? (
                addressDetails.map((entry, index) => (
                  <Grid
                    item
                    xs={12}
                    md={addressDetails.length <= 3 ? 12 / addressDetails.length : 6}
                    lg={addressDetails.length <= 3 ? 12 / addressDetails.length : 4}
                    key={entry.id || `address-${index}`}
                  >
                    {renderAddressDetail(entry, index)}
                  </Grid>
                ))
              ) : (
                <Grid item xs={12}>
                  <MDBox textAlign="center" py={3}>
                    <MDTypography variant="body2" color="text">
                      No address details available
                    </MDTypography>
                  </MDBox>
                </Grid>
              )}
            </Grid>
          </MDBox>
        </Card>

        {/* 🎓 4. Education Details - From edu_edu_details Module */}
        <Card sx={{ boxShadow: 3, mb: 3 }}>
          <MDBox p={3}>
            <MDBox display="flex" alignItems="center" justifyContent="space-between" mb={3}>
              <MDBox display="flex" alignItems="center">
                <Icon fontSize="large" color="info">
                  school
                </Icon>
                <MDTypography variant="h5" fontWeight="bold" ml={1}>
                  Education Details
                </MDTypography>
              </MDBox>
              <MDButton
                variant="gradient"
                color="info"
                size="small"
                onClick={() => setAddEducationDialogOpen(true)}
              >
                <Icon sx={{ mr: 0.5 }}>add</Icon>
                Add Education
              </MDButton>
            </MDBox>
            <Grid container spacing={2}>
              {educationDetails && educationDetails.length > 0 ? (
                educationDetails.map((entry, index) => (
                  <Grid
                    item
                    xs={12}
                    md={educationDetails.length <= 3 ? 12 / educationDetails.length : 6}
                    lg={educationDetails.length <= 3 ? 12 / educationDetails.length : 4}
                    key={entry.id || `education-${index}`}
                  >
                    {renderEducationDetail(entry, index)}
                  </Grid>
                ))
              ) : (
                <Grid item xs={12}>
                  <MDBox textAlign="center" py={3}>
                    <MDTypography variant="body2" color="text">
                      No education details available
                    </MDTypography>
                  </MDBox>
                </Grid>
              )}
            </Grid>
          </MDBox>
        </Card>

        {/* 💼 5. Work Experience - From work_work Module */}
        <Card sx={{ boxShadow: 3, mb: 3 }}>
          <MDBox p={3}>
            <MDBox display="flex" alignItems="center" justifyContent="space-between" mb={3}>
              <MDBox display="flex" alignItems="center">
                <Icon fontSize="large" color="primary">
                  work
                </Icon>
                <MDTypography variant="h5" fontWeight="bold" ml={1}>
                  Work Experience
                </MDTypography>
              </MDBox>
              <MDButton
                variant="gradient"
                color="primary"
                size="small"
                onClick={() => setAddWorkDialogOpen(true)}
              >
                <Icon sx={{ mr: 0.5 }}>add</Icon>
                Add Work Experience
              </MDButton>
            </MDBox>
            <Grid container spacing={2}>
              {workDetails && workDetails.length > 0 ? (
                workDetails
                  .filter(entry => entry && ((entry.companyname && entry.companyname.trim()) || (entry.designation && entry.designation.trim())))
                  .map((entry, index) => (
                    <Grid
                      item
                      xs={12}
                      md={workDetails.length <= 3 ? 12 / workDetails.length : 6}
                      lg={workDetails.length <= 3 ? 12 / workDetails.length : 4}
                      key={entry.id || `work-${index}`}
                    >
                      {renderWorkDetail(entry, index)}
                    </Grid>
                  ))
              ) : (
                <Grid item xs={12}>
                  <MDBox textAlign="center" py={3}>
                    <MDTypography variant="body2" color="text">
                      No work experience available
                    </MDTypography>
                  </MDBox>
                </Grid>
              )}
            </Grid>
          </MDBox>
        </Card>

        {/* 🆔 6. ID Card Details - From id_idcards Module */}
        <Card sx={{ boxShadow: 3, mb: 3 }}>
          <MDBox p={3}>
            <MDBox display="flex" alignItems="center" justifyContent="space-between" mb={3}>
              <MDBox display="flex" alignItems="center">
                <Icon fontSize="large" color="warning">
                  badge
                </Icon>
                <MDTypography variant="h5" fontWeight="bold" ml={1}>
                  ID Card Details
                </MDTypography>
              </MDBox>
              <MDButton
                variant="gradient"
                color="warning"
                size="small"
                onClick={() => setAddIDCardDialogOpen(true)}
                startIcon={<Icon>add</Icon>}
              >
                Add ID Card
              </MDButton>
            </MDBox>
            <Grid container spacing={2}>
              {idCardDetails && idCardDetails.length > 0 ? (
                idCardDetails
                  .filter(entry => entry && ((entry.idtype && entry.idtype.trim()) || (entry.name && entry.name.trim())))
                  .map((entry, index) => (
                    <Grid
                      item
                      xs={12}
                      md={idCardDetails.length <= 3 ? 12 / idCardDetails.length : 6}
                      lg={idCardDetails.length <= 3 ? 12 / idCardDetails.length : 4}
                      key={entry.id || `idcard-${index}`}
                    >
                      {renderIDCardDetail(entry, index)}
                    </Grid>
                  ))
              ) : (
                <Grid item xs={12}>
                  <MDBox textAlign="center" py={3}>
                    <MDTypography variant="body2" color="text">
                      No ID card details available
                    </MDTypography>
                  </MDBox>
                </Grid>
              )}
            </Grid>
          </MDBox>
        </Card>

        {/* 🏦 7. Bank Details - From bank_bankdetails Module */}
        <Card sx={{ boxShadow: 3, mb: 3 }}>
          <MDBox p={3}>
            <MDBox display="flex" alignItems="center" justifyContent="space-between" mb={3}>
              <MDBox display="flex" alignItems="center">
                <Icon fontSize="large" color="success">
                  account_balance
                </Icon>
                <MDTypography variant="h5" fontWeight="bold" ml={1}>
                  Bank Details
                </MDTypography>
              </MDBox>
              <MDButton
                variant="gradient"
                color="success"
                size="small"
                onClick={() => setAddBankDialogOpen(true)}
                startIcon={<Icon>add</Icon>}
              >
                Add Bank Account
              </MDButton>
            </MDBox>
            <Grid container spacing={2}>
              {bankDetails && bankDetails.length > 0 ? (
                bankDetails
                  .filter(entry => entry && ((entry.accountholdername && entry.accountholdername.trim()) || (entry.bankname && entry.bankname.trim())))
                  .map((entry, index) => (
                    <Grid
                      item
                      xs={12}
                      md={bankDetails.length <= 3 ? 12 / bankDetails.length : 6}
                      lg={bankDetails.length <= 3 ? 12 / bankDetails.length : 4}
                      key={entry.id || `bank-${index}`}
                    >
                      {renderBankDetail(entry, index)}
                    </Grid>
                  ))
              ) : (
                <Grid item xs={12}>
                  <MDBox textAlign="center" py={3}>
                    <MDTypography variant="body2" color="text">
                      No bank details available
                    </MDTypography>
                  </MDBox>
                </Grid>
              )}
            </Grid>
          </MDBox>
        </Card>

        {/* 🌐 8. Social Links - From links_sociallinks Module */}
        <Card sx={{ boxShadow: 3, mb: 3 }}>
          <MDBox p={3}>
            <MDBox display="flex" alignItems="center" justifyContent="space-between" mb={3}>
              <MDBox display="flex" alignItems="center">
                <Icon fontSize="large" color="secondary">
                  share
                </Icon>
                <MDTypography variant="h5" fontWeight="bold" ml={1}>
                  Social Media Links
                </MDTypography>
              </MDBox>
              <MDButton
                variant="gradient"
                color="secondary"
                size="small"
                onClick={() => setAddSocialLinkDialogOpen(true)}
                startIcon={<Icon>add</Icon>}
              >
                Add Social Link
              </MDButton>
            </MDBox>
            <Grid container spacing={2}>
              {socialLinksDetails && socialLinksDetails.length > 0 ? (
                socialLinksDetails
                  .filter(entry => entry && entry.type && entry.link)
                  .map((entry, index) => renderSocialLink(entry, index))
              ) : (
                <Grid item xs={12}>
                  <MDBox textAlign="center" py={3}>
                    <MDTypography variant="body2" color="text">
                      No social media links available
                    </MDTypography>
                  </MDBox>
                </Grid>
              )}
            </Grid>
          </MDBox>
        </Card>
      </DialogContent>

      <DialogActions
        sx={{
          backgroundColor: "#f5f5f5",
          borderTop: "2px solid #e0e0e0",
          padding: "24px 32px",
          justifyContent: "space-between",
        }}
      >
        <div style={{ display: 'flex', gap: 8 }}>
          <Button
            onClick={onClose}
            variant="contained"
            sx={{
              backgroundColor: "#757575",
              color: "#fff",
              "&:hover": {
                backgroundColor: "#616161",
              },
              textTransform: "none",
              fontWeight: "bold",
              px: 4,
            }}
          >
            Close
          </Button>
          <Button
            onClick={() => setShareOpen(true)}
            variant="contained"
            sx={{
              backgroundColor: "#9c27b0",
              color: "#fff",
              "&:hover": {
                backgroundColor: "#7b1fa2",
              },
              textTransform: "none",
              fontWeight: "bold",
              px: 3,
            }}
          >
            Share
          </Button>
          {onEdit && (
            <Button
              onClick={() => {
                onEdit(member);
                onClose();
              }}
              variant="contained"
              sx={{
                backgroundColor: "#1976d2",
                color: "#fff",
                "&:hover": {
                  backgroundColor: "#115293",
                },
                textTransform: "none",
                fontWeight: "bold",
                px: 4,
              }}
            >
              Edit
            </Button>
          )}
        </div>
      </DialogActions>

      {/* Education Dialogs */}
      <AddEducationDialog
        open={addEducationDialogOpen}
        onClose={() => setAddEducationDialogOpen(false)}
        sessionId={sessionId}
        userId={parentId}
        onSuccess={(msg) => {
          setSnackbarMessage(msg);
          setSuccessSB(true);
          refreshData();
        }}
        onError={(msg) => {
          setSnackbarMessage(msg);
          setErrorSB(true);
        }}
        onEducationAdded={(updatedList) => setEducationDetails(updatedList)}
      />

      <EditEducationDialog
        open={!!editingEducationEntry}
        onClose={() => setEditingEducationEntry(null)}
        educationEntry={editingEducationEntry}
        sessionId={sessionId}
        userId={parentId}
        onSuccess={(msg) => {
          setSnackbarMessage(msg);
          setSuccessSB(true);
          refreshData();
        }}
        onError={(msg) => {
          setSnackbarMessage(msg);
          setErrorSB(true);
        }}
        onEducationUpdated={(updatedList) => setEducationDetails(updatedList)}
      />

      <DeleteEducationDialog
        open={deleteEducationConfirmOpen}
        onClose={() => {
          setDeleteEducationConfirmOpen(false);
          setEducationToDelete(null);
        }}
        educationEntry={educationToDelete}
        sessionId={sessionId}
        userId={parentId}
        onSuccess={(msg) => {
          setSnackbarMessage(msg);
          setSuccessSB(true);
          refreshData();
        }}
        onError={(msg) => {
          setSnackbarMessage(msg);
          setErrorSB(true);
        }}
        onEducationDeleted={(updatedList) => setEducationDetails(updatedList)}
      />

      {/* Work Experience Dialogs */}
      <AddWorkExperienceDialog
        open={addWorkDialogOpen}
        onClose={() => setAddWorkDialogOpen(false)}
        sessionId={sessionId}
        userId={parentId}
        onSuccess={(msg) => {
          setSnackbarMessage(msg);
          setSuccessSB(true);
          refreshData();
        }}
        onError={(msg) => {
          setSnackbarMessage(msg);
          setErrorSB(true);
        }}
        onWorkAdded={(updatedList) => setWorkDetails(updatedList)}
      />

      <EditWorkExperienceDialog
        open={!!editingWorkEntry}
        onClose={() => setEditingWorkEntry(null)}
        workEntry={editingWorkEntry}
        sessionId={sessionId}
        userId={parentId}
        onSuccess={(msg) => {
          setSnackbarMessage(msg);
          setSuccessSB(true);
          refreshData();
        }}
        onError={(msg) => {
          setSnackbarMessage(msg);
          setErrorSB(true);
        }}
        onWorkUpdated={(updatedList) => setWorkDetails(updatedList)}
      />

      <DeleteWorkExperienceDialog
        open={deleteWorkConfirmOpen}
        onClose={() => {
          setDeleteWorkConfirmOpen(false);
          setWorkToDelete(null);
        }}
        workEntry={workToDelete}
        sessionId={sessionId}
        userId={parentId}
        onSuccess={(msg) => {
          setSnackbarMessage(msg);
          setSuccessSB(true);
          refreshData();
        }}
        onError={(msg) => {
          setSnackbarMessage(msg);
          setErrorSB(true);
        }}
        onWorkDeleted={(updatedList) => setWorkDetails(updatedList)}
      />

      {/* ID Card Dialogs */}
      <AddIDCardDialog
        open={addIDCardDialogOpen}
        onClose={() => setAddIDCardDialogOpen(false)}
        sessionId={sessionId}
        userId={parentId}
        onSuccess={(msg) => {
          setSnackbarMessage(msg);
          setSuccessSB(true);
          refreshData();
        }}
        onError={(msg) => {
          setSnackbarMessage(msg);
          setErrorSB(true);
        }}
        onIDCardAdded={(updatedList) => setIDCardDetails(updatedList)}
      />

      <EditIDCardDialog
        open={!!editingIDCardEntry}
        onClose={() => setEditingIDCardEntry(null)}
        idCardEntry={editingIDCardEntry}
        sessionId={sessionId}
        userId={parentId}
        onSuccess={(msg) => {
          setSnackbarMessage(msg);
          setSuccessSB(true);
          refreshData();
        }}
        onError={(msg) => {
          setSnackbarMessage(msg);
          setErrorSB(true);
        }}
        onIDCardUpdated={(updatedList) => setIDCardDetails(updatedList)}
      />

      <DeleteIDCardDialog
        open={deleteIDCardConfirmOpen}
        onClose={() => {
          setDeleteIDCardConfirmOpen(false);
          setIdCardToDelete(null);
        }}
        idCardEntry={idCardToDelete}
        sessionId={sessionId}
        userId={parentId}
        onSuccess={(msg) => {
          setSnackbarMessage(msg);
          setSuccessSB(true);
          refreshData();
        }}
        onError={(msg) => {
          setSnackbarMessage(msg);
          setErrorSB(true);
        }}
        onIDCardDeleted={(updatedList) => setIDCardDetails(updatedList)}
      />

      {/* Bank Details Dialogs */}
      <AddBankDialog
        open={addBankDialogOpen}
        onClose={() => setAddBankDialogOpen(false)}
        sessionId={sessionId}
        userId={parentId}
        onSuccess={(msg) => {
          setSnackbarMessage(msg);
          setSuccessSB(true);
          refreshData();
        }}
        onError={(msg) => {
          setSnackbarMessage(msg);
          setErrorSB(true);
        }}
        onBankAdded={(updatedList) => setBankDetails(updatedList)}
      />

      <EditBankDialog
        open={!!editingBankEntry}
        onClose={() => setEditingBankEntry(null)}
        bankEntry={editingBankEntry}
        sessionId={sessionId}
        userId={parentId}
        onSuccess={(msg) => {
          setSnackbarMessage(msg);
          setSuccessSB(true);
          refreshData();
        }}
        onError={(msg) => {
          setSnackbarMessage(msg);
          setErrorSB(true);
        }}
        onBankUpdated={(updatedList) => setBankDetails(updatedList)}
      />

      <DeleteBankDialog
        open={deleteConfirmOpen}
        onClose={() => {
          setDeleteConfirmOpen(false);
          setBankToDelete(null);
        }}
        bankEntry={bankToDelete}
        sessionId={sessionId}
        userId={parentId}
        onSuccess={(msg) => {
          setSnackbarMessage(msg);
          setSuccessSB(true);
          refreshData();
        }}
        onError={(msg) => {
          setSnackbarMessage(msg);
          setErrorSB(true);
        }}
        onBankDeleted={(updatedList) => setBankDetails(updatedList)}
      />

      {/* Social Links Dialogs */}
      <AddSocialLinkDialog
        open={addSocialLinkDialogOpen}
        onClose={() => setAddSocialLinkDialogOpen(false)}
        sessionId={sessionId}
        userId={parentId}
        onSuccess={(msg) => {
          setSnackbarMessage(msg);
          setSuccessSB(true);
          refreshData();
        }}
        onError={(msg) => {
          setSnackbarMessage(msg);
          setErrorSB(true);
        }}
        onSocialLinkAdded={(updatedList) => setSocialLinksDetails(updatedList)}
      />

      <EditSocialLinkDialog
        open={!!editingSocialLinkEntry}
        onClose={() => setEditingSocialLinkEntry(null)}
        socialLinkEntry={editingSocialLinkEntry}
        sessionId={sessionId}
        userId={parentId}
        onSuccess={(msg) => {
          setSnackbarMessage(msg);
          setSuccessSB(true);
          refreshData();
        }}
        onError={(msg) => {
          setSnackbarMessage(msg);
          setErrorSB(true);
        }}
        onSocialLinkUpdated={(updatedList) => setSocialLinksDetails(updatedList)}
      />

      <DeleteSocialLinkDialog
        open={deleteSocialLinkConfirmOpen}
        onClose={() => {
          setDeleteSocialLinkConfirmOpen(false);
          setSocialLinkToDelete(null);
        }}
        socialLinkEntry={socialLinkToDelete}
        sessionId={sessionId}
        userId={parentId}
        onSuccess={(msg) => {
          setSnackbarMessage(msg);
          setSuccessSB(true);
          refreshData();
        }}
        onError={(msg) => {
          setSnackbarMessage(msg);
          setErrorSB(true);
        }}
        onSocialLinkDeleted={(updatedList) => setSocialLinksDetails(updatedList)}
      />

      {/* Address Dialogs */}
      <AddAddressDialog
        open={addAddressDialogOpen}
        onClose={() => setAddAddressDialogOpen(false)}
        sessionId={sessionId}
        userId={parentId}
        onSuccess={(msg) => {
          setSnackbarMessage(msg);
          setSuccessSB(true);
          refreshData();
        }}
        onError={(msg) => {
          setSnackbarMessage(msg);
          setErrorSB(true);
        }}
        onAddressAdded={(updatedList) => setAddressDetails(updatedList)}
      />

      <EditAddressDialog
        open={!!editingAddressEntry}
        onClose={() => setEditingAddressEntry(null)}
        addressEntry={editingAddressEntry}
        sessionId={sessionId}
        userId={parentId}
        onSuccess={(msg) => {
          setSnackbarMessage(msg);
          setSuccessSB(true);
          refreshData();
        }}
        onError={(msg) => {
          setSnackbarMessage(msg);
          setErrorSB(true);
        }}
        onAddressUpdated={(updatedList) => setAddressDetails(updatedList)}
      />

      <DeleteAddressDialog
        open={deleteAddressConfirmOpen}
        onClose={() => {
          setDeleteAddressConfirmOpen(false);
          setAddressToDelete(null);
        }}
        addressEntry={addressToDelete}
        sessionId={sessionId}
        userId={parentId}
        onSuccess={(msg) => {
          setSnackbarMessage(msg);
          setSuccessSB(true);
          refreshData();
        }}
        onError={(msg) => {
          setSnackbarMessage(msg);
          setErrorSB(true);
        }}
        onAddressDeleted={(updatedList) => setAddressDetails(updatedList)}
      />

      {/* Snackbars */}
      <MDSnackbar
        color="success"
        icon="check"
        title="Success"
        content={snackbarMessage}
        open={successSB}
        onClose={() => setSuccessSB(false)}
        close={() => setSuccessSB(false)}
        bgWhite
      />
      <MDSnackbar
        color="error"
        icon="warning"
        title="Error"
        content={snackbarMessage}
        open={errorSB}
        onClose={() => setErrorSB(false)}
        close={() => setErrorSB(false)}
        bgWhite
      />

      {/* Share dialog for this family member */}
      <ShareProfileDialog
        open={shareOpen}
        onClose={() => setShareOpen(false)}
        profile={{
          ...member,
          parent_display: parentText || "",
          parents_display: parentText || "",
          spouse_display: spouseName || "",
          children_display: childrenText || "",
        }}
        educationEntries={educationDetails}
        workEntries={workDetails}
        idCardEntries={idCardDetails}
        bankEntries={bankDetails}
        addressEntries={addressDetails}
        onShare={(result) => {
          if (result && result.success) {
            setSnackbarMessage(result.preview ? String(result.preview).slice(0, 300) : 'Shared successfully');
            setSuccessSB(true);
          } else {
            setSnackbarMessage(result && result.message ? result.message : 'Failed to share');
            setErrorSB(true);
          }
        }}
      />
    </Dialog>
  );
};

export default ViewFamilyMemberDialog;
